// src/components/common/Header.tsx
import React, { useState } from 'react'; // useState is already imported
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  Divider // Added for better separation in menu
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle'; // Example icon for logged-in user
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import { Link as RouterLink, useNavigate } from 'react-router-dom'; // Added useNavigate
import { useAuth } from '../../auth/AuthContext'; // Import useAuth

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { isAuthenticated, user, logout } = useAuth(); // Get auth state and functions
  const navigate = useNavigate(); // For navigation to profile, etc.

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout(); // This will also navigate to the login page as defined in AuthContext
    handleMenuClose();
  };

  const navigateTo = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  type MenuItemType = {
    label: string;
    path: string;
    variant?: 'text' | 'contained';
    isButton?: boolean;
  };

  const commonMenuItems: MenuItemType[] = [
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'About Us', path: '/about' },
    { label: 'Help', path: '/help' },
  ];

  const unauthenticatedMenuItems: MenuItemType[] = [
    ...commonMenuItems,
    { label: 'Vendor Login', path: '/vendor/login', variant: 'text' },
    { label: 'Join as Vendor', path: '/vendor/register', variant: 'contained', isButton: true },
  ];

  const authenticatedMenuItems = [
    // You can add common items here too if they should persist, or keep them separate
    { label: 'Dashboard', path: '/vendor/dashboard' },
    // { label: 'My Profile', path: '/vendor/profile' }, // Example
    // Add more authenticated user links here
  ];


  return (
    <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: '1px solid #eaeaea' }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Box display="flex" alignItems="center" component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
            <DeliveryDiningIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700 }}>
              MWM Delivery
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {isMobile ? (
            // Mobile Menu
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
              >
                {isAuthenticated ? <AccountCircle /> : <MenuIcon /> }
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{ mt: '45px' }} // Adjust margin top for better positioning
              >
                {isAuthenticated ? (
                  [
                    <MenuItem key="greeting" disabled sx={{pointerEvents: 'none', opacity: 1}}>
                      <Typography variant="subtitle2">Hi, {user?.name || 'Vendor'}</Typography>
                    </MenuItem>,
                    <Divider key="divider-1" />,
                    ...authenticatedMenuItems.map((item) => (
                      <MenuItem key={item.label} onClick={() => navigateTo(item.path)}>
                        {item.label}
                      </MenuItem>
                    )),
                     // Add common links for authenticated mobile users if needed
                    ...commonMenuItems.map((item) => (
                        <MenuItem key={item.label} onClick={() => navigateTo(item.path)}>
                        {item.label}
                        </MenuItem>
                    )),
                    <Divider key="divider-2" />,
                    <MenuItem key="logout" onClick={handleLogout}>Logout</MenuItem>
                  ]
                ) : (
                  unauthenticatedMenuItems.map((item) => (
                    <MenuItem 
                      key={item.label} 
                      onClick={() => navigateTo(item.path)}
                      sx={item.isButton ? { p: 0 } : {}} // Remove padding for button item
                    >
                      {item.isButton ? (
                        <Button 
                          color="primary" 
                          variant={item.variant || "contained"} // Default to contained if not specified
                          fullWidth 
                          sx={{ m:1, my: 0.5 }} // Add some margin to the button inside menu item
                        >
                          {item.label}
                        </Button>
                      ) : (
                        item.label
                      )}
                    </MenuItem>
                  ))
                )}
              </Menu>
            </>
          ) : (
            // Desktop View
            <Box display="flex" alignItems="center">
              {commonMenuItems.map((item) => (
                <Button key={item.label} color="inherit" component={RouterLink} to={item.path} sx={{ mx: 1 }}>
                  {item.label}
                </Button>
              ))}

              {isAuthenticated ? (
                <>
                  <Typography sx={{ mx: 2 }}>
                    Welcome, {user?.name || 'Vendor'}!
                  </Typography>
                  {authenticatedMenuItems.map((item) => (
                    <Button key={item.label} color="inherit" component={RouterLink} to={item.path} sx={{ mx: 1 }}>
                      {item.label}
                    </Button>
                  ))}
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={handleLogout} 
                    sx={{ ml: 2, borderRadius: 2 }}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button color="inherit" component={RouterLink} to="/vendor/login" sx={{ mx: 1 }}>
                    Vendor Login
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    component={RouterLink} 
                    to="/vendor/register"
                    sx={{ ml: 2, borderRadius: 2 }}
                  >
                    Join as Vendor
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;